/**
 * ZenMarkup Privacy Analytics — Date Picker module.
 *
 * Dropdown-style date range selector with presets and custom range.
 * One trigger button, dropdown with presets + custom range.
 */

import * as state from './state.js';
import { showLoading, showContent } from './ui.js';
import { fetchStats } from './api.js';
import { renderDeadPages, render404Pages } from './insights.js';
import { render as renderJourneys } from './insights-journeys.js';
import { render as renderCharts } from './charts.js';
import { render as renderCampaigns } from './campaigns.js';
import { render as renderOverview } from './overview-widgets.js';
import { render as renderLineChart } from './line-chart.js';
import { render as renderPieChart } from './pie-chart.js';
import { render as renderWidgets } from './widgets.js';
import { render as renderMap } from './map.js';

const PRESETS = [
	{ key: 'today', label: 'Today' },
	{ key: '7d',    label: 'Last 7d' },
	{ key: '30d',   label: 'Last 30d' }
];

const LABELS_FULL  = { today: 'Today', '7d': 'Last 7d', '30d': 'Last 30d', custom: 'Custom Range' };
const LABELS_SHORT = { today: 'Today', '7d': '7d', '30d': '30d', custom: 'Custom' };
const COMPACT_WIDTH = 380;

function labels() {
	return window.innerWidth < COMPACT_WIDTH ? LABELS_SHORT : LABELS_FULL;
}

let container = null;
let trigger = null;
let dropdown = null;
let isOpen = false;

function formatReadable(iso) {
	const parts = iso.split('-');
	const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function currentLabel() {
	const range = state.getRange();
	if (range === 'custom') {
		return formatReadable(state.getStartDate()) + ' – ' + formatReadable(state.getEndDate());
	}
	var l = labels();
	return l[range] || l['7d'];
}

function updateTrigger() {
	if (!trigger) {
		return;
	}
	const span = trigger.querySelector('[data-el="dp-label"]');
	if (span) {
		span.textContent = currentLabel();
	}
}

function toggle() {
	isOpen = !isOpen;
	dropdown.classList.toggle('zmpa-dp-open', isOpen);
	trigger.classList.toggle('zmpa-dp-trigger-open', isOpen);
}

function close() {
	isOpen = false;
	dropdown.classList.remove('zmpa-dp-open');
	trigger.classList.remove('zmpa-dp-trigger-open');
}

async function refreshDashboard() {
	updateTrigger();
	close();
	showLoading();
	const data = await fetchStats();
	if (data) {
		renderDeadPages(data);
		render404Pages(data);
		renderJourneys(data);
		renderCharts(data);
		renderCampaigns(data);
		renderOverview(data);
		renderLineChart(data.timeseries);
		renderPieChart(data.new_returning);
		renderWidgets(data);
		renderMap(data);
	}
	showContent();
}

function loadRange(range) {
	state.setRange(range);
	refreshDashboard();
}

function onCustomApply() {
	const from = dropdown.querySelector('[data-el="date-from"]');
	const to = dropdown.querySelector('[data-el="date-to"]');
	if (!from || !to || !from.value || !to.value || from.value > to.value) {
		return;
	}
	state.setCustomDates(from.value, to.value);
	refreshDashboard();
}

function markActive(key) {
	if (!dropdown) {
		return;
	}
	const items = dropdown.querySelectorAll('[data-el="dp-item"]');
	items.forEach(function (item) {
		item.classList.toggle('zmpa-dp-active', item.getAttribute('data-range') === key);
	});
}

export function init() {
	container = document.querySelector('[data-el="date-range"]');
	if (!container) {
		return;
	}

	container.innerHTML = '';

	trigger = document.createElement('button');
	trigger.type = 'button';
	trigger.className = 'zmpa-dp-trigger';
	trigger.setAttribute('data-el', 'dp-trigger');
	trigger.innerHTML = '<span class="zmpa-dp-label" data-el="dp-label">' + currentLabel() + '</span>'
		+ '<span class="zmpa-dp-arrow">&#9662;</span>';
	trigger.addEventListener('click', function (e) {
		e.stopPropagation();
		toggle();
	});

	dropdown = document.createElement('div');
	dropdown.className = 'zmpa-dp-dropdown';
	dropdown.setAttribute('data-el', 'dp-dropdown');

	PRESETS.forEach(function (p) {
		const item = document.createElement('button');
		item.type = 'button';
		item.className = 'zmpa-dp-item';
		item.setAttribute('data-el', 'dp-item');
		item.textContent = p.label;
		item.setAttribute('data-range', p.key);

		if (p.key === state.getRange()) {
			item.classList.add('zmpa-dp-active');
		}

		item.addEventListener('click', function () {
			markActive(p.key);
			loadRange(p.key);
		});
		dropdown.appendChild(item);
	});

	dropdown.insertAdjacentHTML('beforeend',
		'<div class="zmpa-dp-divider"></div>'
		+ '<div class="zmpa-dp-custom-label">Custom Range</div>'
		+ '<div class="zmpa-dp-custom-row">'
		+ '<input type="date" data-el="date-from" class="zmpa-dp-input" value="' + state.getStartDate() + '">'
		+ '<span class="zmpa-dp-sep">–</span>'
		+ '<input type="date" data-el="date-to" class="zmpa-dp-input" value="' + state.getEndDate() + '">'
		+ '</div>'
		+ '<button type="button" class="zmpa-dp-apply" data-el="dp-apply">Apply</button>'
	);
	dropdown.querySelector('[data-el="dp-apply"]').addEventListener('click', function () {
		markActive('custom');
		onCustomApply();
	});

	container.appendChild(trigger);
	container.appendChild(dropdown);

	document.addEventListener('click', function (e) {
		if (isOpen && !container.contains(e.target)) {
			close();
		}
	});
}
